import { ReactNode, useMemo } from 'react'
import { Trans } from 'react-i18next'

import { Tabs } from '@makyoapp/makyo-ui'

type TTabHeader = {
  headerCn?: string
  navItemStyle?: 'none' | 'default' | 'nav'
  title?: string
  icon?: ReactNode
}

type TTabBody = {
  bodyCn?: string
  children: React.ReactNode
}

export type TTabItems = {
  eventKey: string
  header: TTabHeader
  body: TTabBody
}

export type TTabBodyAdditionalConfig = {
  noPadding?: boolean
  unmountOnExit?: boolean
  mountOnEnter?: boolean
}

type TTabs = {
  activeKey?: string
  tabItems: Array<TTabItems>
  headerNavCn?: string
  onNavigate?: (eventKey: string) => void
  tabBodyProps?: TTabBodyAdditionalConfig
}

export default function TabNavigation(props: TTabs) {
  const { activeKey = '', tabItems, headerNavCn, tabBodyProps, onNavigate } = props

  const navItem = useMemo(
    () =>
      tabItems.map(({ header: { title, icon, navItemStyle }, eventKey }: TTabItems, index: number) => (
        <Tabs.NavItem
          navItemStyle={navItemStyle}
          eventKey={eventKey}
          key={index}
          onClick={() => onNavigate && onNavigate(eventKey)}
        >
          {icon && <>{icon}</>}
          <Trans>{title}</Trans>
        </Tabs.NavItem>
      )),
    [onNavigate, tabItems]
  )

  const panels = useMemo(
    () =>
      tabItems.map(({ body: { bodyCn, children }, eventKey }: TTabItems) => (
        <Tabs.Panel key={eventKey} eventKey={eventKey} className={bodyCn} {...tabBodyProps}>
          {children}
        </Tabs.Panel>
      )),
    [tabBodyProps, tabItems]
  )

  return (
    <Tabs defaultActiveKey={activeKey} activeKey={activeKey}>
      <Tabs.Nav className={headerNavCn}>{navItem}</Tabs.Nav>
      <>{panels}</>
    </Tabs>
  )
}
